// Firestore writers for the think-aloud prototype. Modeled directly on
// ../maffdoku/src/components/Survey/storage.ts — same project, same pattern:
// create-only addDoc() into a collection that a "Stream Firestore to BigQuery"
// extension mirrors into BigQuery within seconds.
//
// Two collections:
//   surveyResponses  — the EXISTING survey collection, already mirrored to BQ. We
//                      reuse it for the consent record (surveyId 'labs-consent-v1')
//                      so consent lands in BQ with zero infra change.
//   labsSessions    — NEW. Holds the full transcript + event capture. To reach
//                      BigQuery this collection must be ADDED to the Stream-to-BQ
//                      extension config in the Firebase console (europe-west2). Until
//                      then docs still persist in Firestore and are exportable.

import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from '../../index'
import { VoiceSession, PuzzleTrace, PuzzleRetro } from './types'
import { CaptureEnv } from '../lib/env'

export type SaveResult = { id: string; collection: string; fellBack: boolean }

// One create-only write per administered retrospective ("aha") follow-up. Written when
// the player answers or skips the retro on a solved puzzle. Absence of a doc for a
// (sessionId, trialIndex) = "not asked". Falls back to `surveyResponses` until the
// `labsRetros` collection has a Firestore create rule (see README).
export async function savePuzzleRetro(retro: PuzzleRetro): Promise<SaveResult> {
  try {
    const doc = await addDoc(collection(db, 'labsRetros'), {
      ...retro,
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'labsRetros', fellBack: false }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      '[labs] write to labsRetros failed (add a Firestore create rule for it — see README). Falling back to surveyResponses.',
      err
    )
    const doc = await addDoc(collection(db, 'surveyResponses'), {
      surveyId: 'labs-retro-v1',
      answers: {},
      hiddenFields: { labsRetro: retro },
      submittedAt: new Date().toISOString(),
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'surveyResponses', fellBack: true }
  }
}

// One create-only write per puzzle attempt (the current model). Written as each puzzle
// finishes — and for aborted attempts on recovery — so nothing is lost to mid-session
// drop-out. Falls back to the permitted `surveyResponses` collection until the
// `labsTraces` collection has a Firestore create rule (see README).
export async function savePuzzleTrace(trace: PuzzleTrace): Promise<SaveResult> {
  try {
    const doc = await addDoc(collection(db, 'labsTraces'), {
      ...trace,
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'labsTraces', fellBack: false }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      '[labs] write to labsTraces failed (add a Firestore create rule for it — see README). Falling back to surveyResponses.',
      err
    )
    const doc = await addDoc(collection(db, 'surveyResponses'), {
      surveyId: 'labs-trace-v2',
      answers: {},
      hiddenFields: { labsTrace: trace },
      submittedAt: new Date().toISOString(),
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'surveyResponses', fellBack: true }
  }
}

// Cryptic-game trace: same create-only pattern + same `labsTraces` collection as the 24
// trace above (Firestore is schemaless; a `domain: 'cryptic'` field on the doc distinguishes
// it downstream). Kept as its own function so the cryptic trace shape stays independently
// typed. Falls back to `surveyResponses` (surveyId 'labs-cryptic-trace-v1') if the rule isn't live.
export async function saveCrypticTrace(trace: { domain: 'cryptic'; sessionId: string } & Record<string, unknown>): Promise<SaveResult> {
  try {
    const doc = await addDoc(collection(db, 'labsTraces'), {
      ...trace,
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'labsTraces', fellBack: false }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[labs] write to labsTraces (cryptic) failed. Falling back to surveyResponses.', err)
    const doc = await addDoc(collection(db, 'surveyResponses'), {
      surveyId: 'labs-cryptic-trace-v1',
      answers: {},
      hiddenFields: { labsTrace: trace },
      submittedAt: new Date().toISOString(),
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'surveyResponses', fellBack: true }
  }
}

// Rebus-game trace: same create-only pattern + `labsTraces` collection (domain: 'rebus'
// distinguishes it downstream). Falls back to `surveyResponses` if the rule isn't live.
export async function saveRebusTrace(trace: { domain: 'rebus'; sessionId: string } & Record<string, unknown>): Promise<SaveResult> {
  try {
    const doc = await addDoc(collection(db, 'labsTraces'), {
      ...trace,
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'labsTraces', fellBack: false }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[labs] write to labsTraces (rebus) failed. Falling back to surveyResponses.', err)
    const doc = await addDoc(collection(db, 'surveyResponses'), {
      surveyId: 'labs-rebus-trace-v1',
      answers: {},
      hiddenFields: { labsTrace: trace },
      submittedAt: new Date().toISOString(),
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'surveyResponses', fellBack: true }
  }
}

// One write per completed session. Primary target is the `labsSessions` collection.
// If that write is denied — which it is until a Firestore security rule for the new
// collection is deployed (see README) — we fall back to the already-permitted
// `surveyResponses` collection so the prototype still captures data. Both paths feed
// the same BigQuery project.
export async function saveVoiceSession(session: VoiceSession): Promise<SaveResult> {
  try {
    const doc = await addDoc(collection(db, 'labsSessions'), {
      ...session,
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'labsSessions', fellBack: false }
  } catch (err) {
    // Most likely permission-denied: the `labsSessions` collection needs a
    // create rule. Log it so it's visible, then fall back so data isn't lost.
    // eslint-disable-next-line no-console
    console.error(
      '[labs] write to labsSessions failed (add a Firestore create rule for it — see README). Falling back to surveyResponses.',
      err
    )
    const doc = await addDoc(collection(db, 'surveyResponses'), {
      surveyId: 'labs-session-v1',
      answers: {},
      hiddenFields: { labsSession: session },
      submittedAt: new Date().toISOString(),
      serverReceivedAt: serverTimestamp(),
    })
    return { id: doc.id, collection: 'surveyResponses', fellBack: true }
  }
}

// Consent record, written the moment the player opts in (before any puzzle) so we
// capture consent even if they drop out immediately. Shape mirrors a SurveyResponse
// so it slots into the existing surveyResponses → BQ pipeline untouched.
export async function saveConsent(params: {
  sessionId: string
  consented: boolean
  env: CaptureEnv
  retroAfterN?: number | null // effective retro regime N (null = 'off'); provenance
  retroCode?: string | null // raw ?s= code seen; provenance
  sttEngine?: string // resolved transcription engine (webspeech | deepgram | google)
  source?: { src: string | null; token: string | null } // acquisition attribution (see sourceTag)
}): Promise<string> {
  const {
    sessionId,
    consented,
    env,
    retroAfterN = null,
    retroCode = null,
    sttEngine = null,
    source = { src: null, token: null },
  } = params
  const doc = await addDoc(collection(db, 'surveyResponses'), {
    surveyId: 'labs-consent-v1',
    answers: { consentVoiceRecording: consented ? 'yes' : 'no' },
    hiddenFields: {
      sessionId,
      gameType: 'labs-24',
      retroAfterN,
      retroCode,
      sttEngine,
      source,
      deviceClass: env.deviceClass,
      browserName: env.browserName,
      osName: env.osName,
      timezone: env.timezone,
      locale: env.locale,
      country: env.country,
      userAgent: env.userAgent,
    },
    submittedAt: new Date().toISOString(),
    serverReceivedAt: serverTimestamp(),
  })
  return doc.id
}

// Re-engagement panel opt-in: links a labs session to a nerdle account so we can invite
// the player to future labs games. Stored in the already-permitted `surveyResponses`
// collection (surveyId 'labs-panel-v1') → BigQuery — SEPARATE from the anonymous
// `labsTraces` corpus, which stays anonymous. `lblToken` is a nerdle Bearer token that
// resolves to the account server-side; `email` is captured directly for convenience.
export async function savePanelSignup(params: {
  sessionId: string
  method: 'logged-in' | 'google' | 'email' // how the identity was obtained
  lblToken?: string | null
  email?: string | null
  marketingOptIn?: boolean
}): Promise<string> {
  const doc = await addDoc(collection(db, 'surveyResponses'), {
    surveyId: 'labs-panel-v1',
    answers: {
      method: params.method,
      marketingOptIn: params.marketingOptIn ?? true,
    },
    hiddenFields: {
      sessionId: params.sessionId,
      gameType: 'labs-24',
      method: params.method,
      lblToken: params.lblToken ?? null,
      email: params.email ?? null,
    },
    submittedAt: new Date().toISOString(),
    serverReceivedAt: serverTimestamp(),
  })
  return doc.id
}
